import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { isAgeBand, type AgeBand } from './ageSettings';

const STORAGE_KEY = 'enklespill:profile:v1';

/**
 * One child on this device. Deliberately minimal (no name, no birthday) and shaped like a
 * row a backend could store later: a stable id, timestamps, and only what the game needs.
 * A future parent account would own a list of these and sync them; the child would pick
 * their own picture instead of typing a password.
 */
export interface ChildProfile {
  id: string;
  ageBand: AgeBand;
  /** Gentle "the truck is tired" stop after a few levels. Parents can switch it off. */
  pauseReminder: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProfileState {
  profile: ChildProfile | null;
  setAgeBand: (band: AgeBand) => void;
  setPauseReminder: (on: boolean) => void;
}

function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch {
    // fall through
  }
  return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createProfile(band: AgeBand, now = new Date()): ChildProfile {
  const iso = now.toISOString();
  return { id: newId(), ageBand: band, pauseReminder: true, createdAt: iso, updatedAt: iso };
}

/** Accept only a well-formed stored profile; anything else means "ask the parent again". */
export function migrateProfile(persisted: unknown): { profile: ChildProfile | null } {
  const raw = (persisted as { profile?: Partial<ChildProfile> } | null)?.profile;
  if (!raw || typeof raw !== 'object' || !isAgeBand(raw.ageBand) || typeof raw.id !== 'string') {
    return { profile: null };
  }
  return {
    profile: {
      id: raw.id,
      ageBand: raw.ageBand,
      pauseReminder: raw.pauseReminder !== false,
      createdAt: raw.createdAt ?? new Date().toISOString(),
      updatedAt: raw.updatedAt ?? new Date().toISOString(),
    },
  };
}

export const useProfileStore = create<ProfileState>()(
  persist(
    (set, get) => ({
      profile: null,
      setAgeBand: (band) => {
        const current = get().profile;
        set({
          profile: current
            ? { ...current, ageBand: band, updatedAt: new Date().toISOString() }
            : createProfile(band),
        });
      },
      setPauseReminder: (on) => {
        const current = get().profile;
        if (!current) return;
        set({ profile: { ...current, pauseReminder: on, updatedAt: new Date().toISOString() } });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 1,
      migrate: (persisted) => migrateProfile(persisted),
      partialize: (state) => ({ profile: state.profile }),
      merge: (persisted, current) => ({ ...current, ...migrateProfile(persisted) }),
    }
  )
);

/** Current age band, or null before the parent has picked one. */
export function currentAgeBand(): AgeBand | null {
  return useProfileStore.getState().profile?.ageBand ?? null;
}
